import { LayoutGrid } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { boardPath } from '@/lib/board/boardFilters'
import { FEATURES } from '@/lib/featureFlags'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { PROCEDURE_TYPE_LABELS, type ProcedureType } from '@/types/cgt'

/**
 * The grid icon that opens the Overall Status board.
 *
 * In the top bar it opens every procedure type. Beside a page title it opens
 * the board filtered to that page's type. Rendered only for users who can open
 * the board, so it never leads to /no-access.
 */
export function OverallStatusLink({
  type,
  placement,
}: {
  type?: ProcedureType
  placement: 'topbar' | 'title'
}) {
  const { can } = usePermission()
  const enabled = useFeatureFlag(FEATURES.procedures)
  if (!enabled || !can(PERMISSIONS.proceduresRead)) return null

  const label = type ? `Overall Status: ${PROCEDURE_TYPE_LABELS[type]}` : 'Overall Status'
  return (
    <Link
      to={boardPath(type)}
      title={label}
      aria-label={label}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md transition-colors',
        placement === 'topbar'
          ? 'h-8 w-8 text-muted-foreground hover:bg-muted hover:text-foreground'
          : 'h-9 w-9 bg-slate-500/90 text-white hover:bg-slate-600',
      )}
    >
      <LayoutGrid className={placement === 'topbar' ? 'h-4 w-4' : 'h-5 w-5'} />
    </Link>
  )
}
